import { ReactNode, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material/styles'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useFlags } from '@equipmentshare/feature-flags'
import { heapEvent } from '@equipmentshare/heaper'
import { StorePart } from '../../../../types/storePart'
import { formatCurrency, formatMoney } from '../../../helpers/utils'
import CommitmentsDrawer, { CommitmentTab, getCommitmentCounts } from './CommitmentsDrawer'

type StatProps = {
	label: string,
	value: ReactNode,
	accent?: boolean,
	dim?: boolean,
	onClick?: () => void,
	/** Named area in the parent grid — see STAT_GRID_AREAS. */
	area?: string,
}

function Stat( { label, value, accent, dim, onClick, area }: StatProps ) {
	const valueNode = (
		<Typography
			component="span"
			color={dim ? 'text.disabled' : undefined}
			sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, fontSize: accent ? 32 : 21, fontWeight: accent ? 700 : 500 }}
		>
			{value}
		</Typography>
	)

	return (
		<Box sx={{ gridArea: area }}>
			<Typography
				variant="caption"
				color={accent ? 'text.primary' : 'text.secondary'}
				sx={{ display: 'block', fontWeight: accent ? 600 : undefined }}
			>
				{label}
			</Typography>
			<Box sx={{ mt: 0.25 }}>
				{onClick ? (
					<ButtonBase
						onClick={onClick}
						sx={{ 'display': 'inline-flex', 'alignItems': 'center', 'gap': 0.25, 'borderRadius': 1, '&:hover': { color: 'primary.main' } }}
					>
						{valueNode}
						<ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
					</ButtonBase>
				) : valueNode}
			</Box>
		</Box>
	)
}

/** Column gap between tiles, in theme spacing units. The divider sits at its exact midpoint. */
const TILE_GAP = 2.5

/** Tile placement by name, so the same children arrange differently at each breakpoint.
    Three groups: On hand (here now) · Committed (spoken-for / leaving) · Incoming (on order). */
const STAT_GRID_AREAS = {
	xs: [
		'"onHand    onHand"',
		'"owned     available"',
		'"divider1  divider1"',
		'"committed committed"',
		'"allocated outgoing"',
		'"onRent    ."',
		'"divider2  divider2"',
		'"incoming  incoming"',
		'"ordered   ."',
	].join( ' ' ),
	md: [
		'"onHand onHand    committed committed committed incoming"',
		'"owned  available allocated outgoing  onRent    ordered"',
	].join( ' ' ),
}

/**
 * Current-state snapshot: owned / available / allocated / on-rent / ordered / outgoing
 * quantities plus the cost block. The four commitment counts are clean numbers that open
 * the roomy Commitments drawer (replacing the cramped popovers — see CommitmentsDrawer).
 */
export default function StatePanel( { item }: { item: StorePart } ) {
	const flags = useFlags()
	const { quantity, availableQuantity } = item
	const counts = getCommitmentCounts( item )

	// The group separator turns from a column rule into a horizontal rule when the tiles stack.
	const isWide = useMediaQuery( ( theme: Theme ) => theme.breakpoints.up( 'md' ) )

	const [ openTab, setOpenTab ] = useState<CommitmentTab | null>( null )

	const openCommitments = ( tab: CommitmentTab ) => () => {
		heapEvent( 'Inventory - Item Details - Click - Commitments', { commitment_type: tab } )
		setOpenTab( tab )
	}

	// A commitment tile is only interactive when it has detail to show.
	const commitmentTile = ( tab: CommitmentTab, count: number ) => (
		count > 0
			? { value: count.toLocaleString( 'en-US' ), onClick: openCommitments( tab ) }
			: { value: '0', dim: true }
	)

	const detailRows: [ string, string ][] = [
		[ 'Most recent cost', item.cost ? formatCurrency( item.cost.toString() ) : 'N/A' ],
		...( flags.enableTswWeightedAverageCost
			? [ [ 'Weighted average', item.weightedAverageCost ? formatMoney( item.weightedAverageCost ) : 'N/A' ] as [ string, string ] ]
			: [] ),
		[ 'Value in inventory', formatCurrency( ( item.cost ? item.cost * quantity : 0 ).toString() ) ],
		// Stocking levels — labelled to match the Minimum/Maximum fields on the edit form.
		[ 'Minimum', item.threshold?.toLocaleString( 'en-US' ) ?? 'N/A' ],
		[ 'Maximum', item.max?.toLocaleString( 'en-US' ) ?? 'N/A' ],
	]

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: 'stretch',
					border: 1,
					borderColor: 'divider',
					borderRadius: 1,
					bgcolor: 'background.paper',
					overflow: 'hidden',
				}}
			>
				{/* All six tiles live in ONE grid, so every tile column is the same width and every
				    gap between them is the same size — see STAT_GRID_AREAS. */}
				<Box
					sx={{
						flex: 1,
						p: 2.5,
						display: 'grid',
						gridTemplateColumns: {
							xs: 'repeat(2, minmax(0, 1fr))',
							md: 'repeat(6, minmax(0, 1fr))',
						},
						gridTemplateAreas: STAT_GRID_AREAS,
						columnGap: TILE_GAP,
						rowGap: 1.5,
						alignContent: 'start',
						// Center each block in its row so every number sits on the same line, despite the
						// larger hero value in Total owned.
						alignItems: 'center',
					}}
				>
					<Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ gridArea: 'onHand' }}>
						On hand
					</Typography>
					<Stat area="owned" label="Total owned" value={quantity.toLocaleString( 'en-US' )} accent />
					<Stat area="available" label="Available" value={availableQuantity.toLocaleString( 'en-US' )} />
					{/* Each divider sits in a gutter: on md it's placed in the following column then shifted
					    half a gap left so it lands mid-gutter without consuming a column of its own. */}
					<Divider
						orientation={isWide ? 'vertical' : 'horizontal'}
						sx={{
							gridArea: { xs: 'divider1', md: '1 / 3 / -1 / 4' },
							justifySelf: { md: 'start' },
							ml: { md: TILE_GAP / -2 },
							// Keep full height even though the grid centers its items.
							alignSelf: 'stretch',
						}}
					/>
					<Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ gridArea: 'committed' }}>
						Committed
					</Typography>
					<Stat area="allocated" label="Allocated" {...commitmentTile( 'allocated', counts.allocated )} />
					<Stat area="outgoing" label="Outgoing" {...commitmentTile( 'outgoing', counts.outgoing )} />
					<Stat area="onRent" label="On rent" {...commitmentTile( 'onRent', counts.onRent )} />
					<Divider
						orientation={isWide ? 'vertical' : 'horizontal'}
						sx={{
							gridArea: { xs: 'divider2', md: '1 / 6 / -1 / 7' },
							justifySelf: { md: 'start' },
							ml: { md: TILE_GAP / -2 },
							alignSelf: 'stretch',
						}}
					/>
					<Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ gridArea: 'incoming' }}>
						Incoming
					</Typography>
					<Stat area="ordered" label="Ordered" {...commitmentTile( 'ordered', counts.ordered )} />
				</Box>
				<Box
					sx={{
						p: 2.5,
						minWidth: { md: 240 },
						display: 'grid',
						gap: 1,
						bgcolor: 'grey.50',
						// Longhand width/style, NOT the `borderTop`/`borderLeft` shorthand: a responsive
						// shorthand emits `border-*: 1px solid` inside a media query, which resets
						// border-*-color to currentColor and clobbers `borderColor` below.
						borderColor: 'divider',
						borderTopStyle: 'solid',
						borderLeftStyle: 'solid',
						borderTopWidth: { xs: '1px', md: 0 },
						borderLeftWidth: { xs: 0, md: '1px' },
					}}
				>
					{detailRows.map( ( [ key, value ] ) => (
						<Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
							<Typography variant="body2" color="text.secondary">{key}</Typography>
							<Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
						</Box>
					) )}
				</Box>
			</Box>

			<CommitmentsDrawer
				open={openTab !== null}
				activeTab={openTab ?? 'allocated'}
				onTabChange={setOpenTab}
				onClose={() => setOpenTab( null )}
				item={item}
			/>
		</>
	)
}
